// Область доступа (FR-013, data-model.md, access.assignment_areas).
import { z } from '../kernel/zod.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { isInstallationOnlyRole } from '../rights/roles.ts';
import type { Role } from '../rights/roles.ts';

export const areaKinds = [
  'installation',
  'legal_entity',
  'object',
  'document_type',
  'document',
] as const;
export const AreaKindSchema = z.enum(areaKinds);
export type AreaKind = (typeof areaKinds)[number];

/** Виды, у которых есть номер записи: юрлицо группы, объект, тип документа, документ. */
export const refAreaKinds = ['legal_entity', 'object', 'document_type', 'document'] as const;

/** refId пуст только у «всей установки»; у остальных — номер записи (в 001 — только формат). */
export const AreaSchema = z.discriminatedUnion(
  'kind',
  [
    z.object({ kind: z.literal('installation') }),
    z.object({ kind: z.enum(refAreaKinds), refId: z.uuid({ error: fieldMessages.area }) }),
  ],
  { error: fieldMessages.area },
);

export type Area = z.infer<typeof AreaSchema>;

export const INSTALLATION_AREA = { kind: 'installation' } as const satisfies Area;

/**
 * Ключ области для area_keys (FR-013б): installation, legal_entity:<id>, object:<id>,
 * document_type:<id>, document:<id>. Назначение покрывает запись, если хотя бы один ключ
 * его областей есть в area_keys записи.
 */
export function areaKey(area: Area): string {
  return area.kind === 'installation' ? 'installation' : `${area.kind}:${area.refId}`;
}

/** Области назначения: хотя бы одна (FR-013а). */
export const AreasInputSchema = z.array(AreaSchema, { error: fieldMessages.areas }).min(1, {
  error: fieldMessages.areas,
});

/** У администратора клиента и сопровождения — только «вся установка» (FR-013в). */
export function areasFitRole(role: Role, areas: readonly Area[]): boolean {
  if (!isInstallationOnlyRole(role)) return true;
  return areas.length === 1 && areas[0]?.kind === 'installation';
}
